// src/components/checkout/RequestOrderForm.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = 'https://madadline.com/api';

const MOROCCAN_CITIES = [
  { value: 'Casablanca', key: 'casablanca' },
  { value: 'Rabat',      key: 'rabat' },
  { value: 'Fès',        key: 'fes' },
  { value: 'Marrakech',  key: 'marrakech' },
  { value: 'Tanger',     key: 'tanger' },
  { value: 'Agadir',     key: 'agadir' },
  { value: 'Meknès',     key: 'meknes' },
  { value: 'Oujda',      key: 'oujda' },
  { value: 'Kénitra',    key: 'kenitra' },
  { value: 'Tétouan',    key: 'tetouan' },
  { value: 'Salé',       key: 'sale' },
  { value: 'Autre',      key: 'autre' },
];

function RequestOrderForm({ cartItems = [], subTotal = 0, shippingFee = 0, onSuccess }) {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    city: '',
    address: '',
    special_instructions: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.customer_name.trim())  return t('request.errorName');
    if (!form.customer_phone.trim()) return t('request.errorPhone');
    if (!form.city)                  return t('request.errorCity');
    if (!form.address.trim())        return t('request.errorAddress');
    if (!cartItems.length)           return t('request.errorCart');
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const v = validate();
    if (v) { setError(v); return; }

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        customer_name:        form.customer_name.trim(),
        customer_phone:       form.customer_phone.trim(),
        city:                 form.city,
        address:              form.address.trim(),
        country:              'Maroc',
        currency:             'MAD',
        special_instructions: form.special_instructions.trim() || null,
        cart_items: cartItems.map(i => ({
          product_id: i.product?.id ?? i.product_id,
          quantity:   i.quantity,
          size:       i.size  || null,
          color:      i.color || null,
          material:   i.material || null,
        })),
      };

      const { data } = await axios.post(`${API}/orders/request`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (data?.wa_url) {
        window.open(data.wa_url, '_blank', 'noopener,noreferrer');
      }

      if (typeof onSuccess === 'function') onSuccess(data);
    } catch (err) {
      console.error('Erreur commande sur commande:', err?.response?.data || err);
      const serverErrors = err.response?.data?.errors;
      const firstError = serverErrors
        ? Object.values(serverErrors)[0]?.[0]
        : (err.response?.data?.error || err.response?.data?.message);
      setError(firstError || t('request.errorSubmit'));
    } finally {
      setLoading(false);
    }
  };

  const estimate = subTotal + shippingFee;
  const hasMissingOptions = cartItems.some(i => !i.size || !i.color);

  return (
    <form className="ro-form" onSubmit={handleSubmit}>
      <p className="ro-form__intro">{t('request.intro')}</p>

      <div className="ro-form__grid">
        <label>
          <span>{t('request.name')} *</span>
          <input type="text" value={form.customer_name} onChange={set('customer_name')} autoComplete="name" required />
        </label>

        <label>
          <span>{t('request.phone')} *</span>
          <input type="tel" value={form.customer_phone} onChange={set('customer_phone')} placeholder="06 12 34 56 78" autoComplete="tel" required />
        </label>

        <label>
          <span>{t('request.city')} *</span>
          <select value={form.city} onChange={set('city')} required>
            <option value="">{t('checkout.selectCity')}</option>
            {MOROCCAN_CITIES.map(c => (
              <option key={c.value} value={c.value}>{t(`checkout.cities.${c.key}`)}</option>
            ))}
          </select>
        </label>

        <label className="ro-form__full">
          <span>{t('request.address')} *</span>
          <input type="text" value={form.address} onChange={set('address')} autoComplete="street-address" required />
        </label>

        <label className="ro-form__full">
          <span>{t('request.instructions')}</span>
          <textarea
            rows={3}
            value={form.special_instructions}
            onChange={set('special_instructions')}
            placeholder={t('request.instructionsPlaceholder')}
          />
        </label>
      </div>

      {hasMissingOptions && (
        <div className="ro-form__hint">ⓘ {t('request.hintMissingOptions')}</div>
      )}

      {error && <div className="ro-form__error">{error}</div>}

      <div className="ro-form__summary">
        <span>{t('request.estimateLabel')}</span>
        <strong>{estimate.toFixed(0)} DH</strong>
      </div>
      <p className="ro-form__estimate-note">{t('request.estimateNote')}</p>

      <button type="submit" className="ro-form__submit" disabled={loading}>
        {loading ? t('request.sending') : t('request.sendWhatsapp')}
      </button>

      <p className="ro-form__note">{t('request.note')}</p>
    </form>
  );
}

export default RequestOrderForm;